/**
 * Form sections for NodeConfigPanel.
 *
 * Presentational pieces split out of the panel to keep it under the file-size
 * limit: the full company address block (CR-6691), the city/country location
 * block for other node types, contact info, and the parent picker.
 */

import { Globe, Mail, Link as LinkIcon, MapPin } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { NODE_TYPE_LABELS } from './types';
import { CURRENCY_OPTIONS } from '../../pages/suppliers/wizard/types';

const SECTION_HEADING =
  'font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2';

/** Parent group/division picker shown for top-level division/company creates. */
export function ParentPicker({
  parentIsRequired,
  parentGroupId,
  availableGroups,
  unparentedValue,
  onChange,
}: {
  parentIsRequired: boolean;
  parentGroupId: string;
  availableGroups: Array<{ id: number; name: string; level: number }>;
  unparentedValue: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="col-span-2">
      <Label htmlFor="parent-group">
        Parent{parentIsRequired ? ` ${NODE_TYPE_LABELS['group']} *` : ' (optional)'}
      </Label>
      <Select value={parentGroupId} onValueChange={onChange}>
        <SelectTrigger id="parent-group">
          <SelectValue
            placeholder={
              parentIsRequired
                ? `Select the parent ${NODE_TYPE_LABELS['group'].toLowerCase()}`
                : `Top level — no parent`
            }
          />
        </SelectTrigger>
        <SelectContent>
          {!parentIsRequired && (
            <SelectItem value={unparentedValue}>Top level — no parent</SelectItem>
          )}
          {availableGroups.map((g) => (
            <SelectItem key={g.id} value={String(g.id)}>
              {g.name}
              <span className="ml-2 text-xs text-muted-foreground">
                {g.level === 0 ? NODE_TYPE_LABELS['group'] : NODE_TYPE_LABELS['division']}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="mt-1 text-xs text-muted-foreground">
        {parentIsRequired
          ? `A ${NODE_TYPE_LABELS['division'].toLowerCase()} sits inside a ${NODE_TYPE_LABELS['group'].toLowerCase()}.`
          : `A ${NODE_TYPE_LABELS['company'].toLowerCase()} can sit under a ${NODE_TYPE_LABELS['group'].toLowerCase()} or a ${NODE_TYPE_LABELS['division'].toLowerCase()}, or stand on its own.`}
      </p>
    </div>
  );
}

// CR-6691: full structured address for companies, matching the address fields
// captured elsewhere in the platform (supplier/company profiles).
export interface CompanyAddressValues {
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateCounty: string;
  postalCode: string;
  country: string;
}

/** Full address block shown for company nodes. */
export function CompanyAddressSection({
  values,
  onChange,
}: {
  values: CompanyAddressValues;
  onChange: (field: keyof CompanyAddressValues, value: string) => void;
}) {
  return (
    <div className="space-y-4">
      <h3 className={SECTION_HEADING}>
        <MapPin className="h-4 w-4" />
        Address
      </h3>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label htmlFor="address-line-1">Address Line 1</Label>
          <Input
            id="address-line-1"
            value={values.addressLine1}
            onChange={(e) => onChange('addressLine1', e.target.value)}
            placeholder="Street address"
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="address-line-2">Address Line 2</Label>
          <Input
            id="address-line-2"
            value={values.addressLine2}
            onChange={(e) => onChange('addressLine2', e.target.value)}
            placeholder="Suite, unit, building (optional)"
          />
        </div>
        <div>
          <Label htmlFor="address-city">City</Label>
          <Input
            id="address-city"
            value={values.city}
            onChange={(e) => onChange('city', e.target.value)}
            placeholder="City"
          />
        </div>
        <div>
          <Label htmlFor="address-state">State / County</Label>
          <Input
            id="address-state"
            value={values.stateCounty}
            onChange={(e) => onChange('stateCounty', e.target.value)}
            placeholder="State / County"
          />
        </div>
        <div>
          <Label htmlFor="address-postal-code">Postal Code</Label>
          <Input
            id="address-postal-code"
            value={values.postalCode}
            onChange={(e) => onChange('postalCode', e.target.value)}
            placeholder="Postal code"
          />
        </div>
        <div>
          <Label htmlFor="address-country">Country</Label>
          <Input
            id="address-country"
            value={values.country}
            onChange={(e) => onChange('country', e.target.value)}
            placeholder="Country"
          />
        </div>
      </div>
    </div>
  );
}

/** City/country location block for non-company node types. */
export function LocationSection({
  city,
  country,
  onCityChange,
  onCountryChange,
}: {
  city: string;
  country: string;
  onCityChange: (value: string) => void;
  onCountryChange: (value: string) => void;
}) {
  return (
    <div className="space-y-4">
      <h3 className={SECTION_HEADING}>
        <Globe className="h-4 w-4" />
        Location
      </h3>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            value={city}
            onChange={(e) => onCityChange(e.target.value)}
            placeholder="City"
          />
        </div>
        <div>
          <Label htmlFor="country">Country</Label>
          <Input
            id="country"
            value={country}
            onChange={(e) => onCountryChange(e.target.value)}
            placeholder="Country"
          />
        </div>
      </div>
    </div>
  );
}

/** Email/website contact block for group and company nodes. */
export function ContactSection({
  showEmail,
  primaryEmail,
  website,
  onEmailChange,
  onWebsiteChange,
}: {
  showEmail: boolean;
  primaryEmail: string;
  website: string;
  onEmailChange: (value: string) => void;
  onWebsiteChange: (value: string) => void;
}) {
  return (
    <div className="space-y-4">
      <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
        Contact Information
      </h3>

      <div className="grid grid-cols-2 gap-4">
        {showEmail && (
          <div>
            {/* CR-6247: named "Company Email" to match the label the Company
                Overview tab reads it back under. Only rendered for company
                nodes (showEmail), so a group's block is unaffected. */}
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-3 w-3" /> Company Email *
            </Label>
            <Input
              id="email"
              type="email"
              value={primaryEmail}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="contact@company.com"
            />
          </div>
        )}

        <div className={showEmail ? '' : 'col-span-2'}>
          <Label htmlFor="website" className="flex items-center gap-2">
            <LinkIcon className="h-3 w-3" /> Website
          </Label>
          <Input
            id="website"
            value={website}
            onChange={(e) => onWebsiteChange(e.target.value)}
            placeholder="https://www.example.com"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Base currency for a company being created from the org chart.
 *
 * Shown on create only: a site's currency is either its own (Override) or
 * inherited from its group, and that choice lives in Admin Setup > Regional
 * once the site exists. Picking one here sets the site to Override so the
 * currency takes effect immediately instead of lying dormant behind the
 * platform default.
 */
export function CurrencySection({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-4">
      <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
        Regional
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="base_currency">Base Currency</Label>
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger id="base_currency" aria-label="Base Currency">
              <SelectValue placeholder="Select currency..." />
            </SelectTrigger>
            <SelectContent>
              {CURRENCY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Reporting currency for this site. Change it later under Admin Setup &gt; Regional.
          </p>
        </div>
      </div>
    </div>
  );
}
